import { Component, Input, OnInit } from '@angular/core';
import { HttpCommunicationService } from '../../../../service/http-communication.service';
import { BulkNotificationDto, CommunicationFrequencyTypeEnum, CommunicationReminderBeforeEnum, CommunicationTriggerTypeEnum, DynamicFormElementDto, DynamicPropertyDto, FilterOperatorEnum, NotificationBulkTriggerDto } from '../../../../service/dto';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';

interface CommunicationFrequency {
	value: CommunicationFrequencyTypeEnum;
	label: string;
}
interface CommunicationTriggerType {
	value: CommunicationTriggerTypeEnum;
	label: string;
}
interface CommunicationReminderBefore {
	value: CommunicationReminderBeforeEnum;
	label: string;
}

export const futureDateValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
	if (!control.value) return null; // allow empty value if required handled elsewhere

	const inputDate = new Date(control.value);
	inputDate.setHours(0, 0, 0, 0);

	const today = new Date();
	today.setHours(0, 0, 0, 0);

	return inputDate <= today ? { invalidDate: 'Date must be in the future' } : null;
};

@Component({
	selector: 'app-schedule-modal',
	standalone: false,
	templateUrl: './schedule-modal.component.html',
	styleUrl: './schedule-modal.component.scss'
})
export class ScheduleModalComponent implements OnInit {

	@Input() bulkPayload!: BulkNotificationDto;
	// @Input() schedule!: CommunicationScheduleDto;
	form!: FormGroup;

	frequencies: CommunicationFrequency[] = [
		{ value: 'FirstDayOfMonth', label: 'First day of the month' },
		{ value: 'FirstWorkingDayOfMonth', label: 'First working day of the month' },
		{ value: 'LastDayOfMonth', label: 'Last day of the month' },
		{ value: 'LastWorkingDayOfMonth', label: 'Last working day of the month' },
		{ value: 'Daily', label: 'Every day' },
		{ value: 'Weekly', label: 'Every week' },
		{ value: 'Fortnight', label: 'Every 2 weeks' },
		{ value: 'Monthly', label: 'Every month' },
		{ value: 'Quarterly', label: 'Every 3 months' },
		{ value: 'BiAnnually', label: 'Every 6 months' },
		{ value: 'Annually', label: 'Every year' }
	];

	
	triggerTypes: CommunicationTriggerType[] = [
		{ value: 'DateOfBirth', label: 'Birth Date' },
		{ value: 'PolicyRenewalReminder', label: 'Policy Renewal Reminder' },
		{ value: 'Recurring', label: 'Recurring Schedule' },
	];

	reminderBeforeTypes: CommunicationReminderBefore[] = [
		{ value: 'Day', label: 'Day' },
		{ value: 'Week', label: 'Week' },
		{ value: 'Month', label: 'Month' },
	];

	constructor(
		private fb: FormBuilder,
		private httpService: HttpCommunicationService,
		private modalRef: NzModalRef,
		private message: NzMessageService) {

			this.form = this.fb.group({
				triggerName: ['', [Validators.required]],
				triggerDescription: [''],
				triggerType: ['DateOfBirth', Validators.required],
				recurring: this.fb.group({
					frequency: undefined, 
				}),
				reminderBefore: ['Day'],
				reminderBeforeValue: [1, [Validators.pattern(/^\d+$/)]],
				startDate: [this.getDefaultStartDate(), [futureDateValidator]],
				time: [this.getDefaultTime()]
			});
	}

	ngOnInit(): void {
		if (this.bulkPayload) {

			var startDate;
			if (this.bulkPayload.bulk && this.bulkPayload.bulk.nextRunDate) {
				startDate = this.bulkPayload.bulk.nextRunDate;
			} else {
				startDate = this.getDefaultStartDate();
			}

			var triggerTime;
			if (this.bulkPayload.trigger && this.bulkPayload.trigger.triggerTime) {
				triggerTime = this.parseTimeString(this.bulkPayload.trigger.triggerTime);
			} else {
				triggerTime = this.getDefaultTime();
			}

			this.form.patchValue({
				triggerName: this.bulkPayload.trigger ? this.bulkPayload.trigger.triggerName : undefined,
				triggerDescription: this.bulkPayload.trigger ? this.bulkPayload.trigger.triggerDescription : undefined,
				triggerType: this.bulkPayload.trigger ? this.bulkPayload.trigger.triggerType : 'Dob',
				recurring: {
					frequency: this.bulkPayload.trigger ? this.bulkPayload.trigger.frequency : undefined
				},
				reminderBefore: this.bulkPayload.trigger ? this.bulkPayload.trigger.reminderBefore : undefined,
				reminderBeforeValue: this.bulkPayload.trigger ? this.bulkPayload.trigger.reminderBeforeValue : undefined,
				startDate: startDate,
				time: triggerTime
			});
		}

		this.form.get('triggerType')?.valueChanges.subscribe((value: string) => {
			const triggerNameControl = this.form.get('triggerName');
			const selectedType = this.triggerTypes.find(t => t.value === value);

			if (triggerNameControl && !triggerNameControl.value && selectedType) {
				triggerNameControl.setValue(selectedType.label);
			}
		});
	}

	private getDefaultStartDate(): Date {
		const date = new Date();
		date.setDate(date.getDate() + 1); // next day
		return date;
	}

	private getDefaultTime(): Date {
		const date = new Date();
		date.setHours(8, 0, 0, 0); // 08:00:00
		return date;
	}

	disablePastDates = (current: Date): boolean => {
		const today = new Date();
		today.setHours(0, 0, 0, 0); // normalize to midnight
		return current <= today; // disable today and all past dates
	};

	get recurringForm(): FormGroup {
  		return this.form.get('recurring') as FormGroup;
	}

	isRecurring(): boolean {
  		const value: string = this.form.get('triggerType')?.value;
  		return value === 'Recurring';
	}

	isPolicyRenewalReminder(): boolean {
  		const value: string = this.form.get('triggerType')?.value;
  		return value === 'PolicyRenewalReminder';
	}

	onSubmit() {
		if (this.form.valid) {

			if (!this.form.value.triggerName) {
				this.message.error("Please capture a name");
				return;
			}

			if (this.isRecurring()) {
				//Make sure we have frequency, days and time (not sure if time should be optional?)
				if (!this.form.value.recurring) {
					this.message.error("Please capture the recurring schedule fields");
					return;
				}
				if (!this.form.value.recurring.frequency) {
					this.message.error("Please choose a frequency");
					return;
				}
			}

			if (!this.form.value.time) {
				this.message.error("Please choose a time");
				return;
			}

			this.bulkPayload.trigger = {
				triggerName: this.form.value.triggerName,
				triggerDescription: this.form.value.triggerDescription,
				triggerType: this.form.value.triggerType,
				frequency: this.isRecurring() ? this.form.value.recurring?.frequency : undefined,
				reminderBefore: this.isPolicyRenewalReminder() ? this.form.value.reminderBefore : undefined,
				reminderBeforeValue: this.isPolicyRenewalReminder() ? this.form.value.reminderBeforeValue : undefined,
				triggerTime: this.getTimeAsString(),
				description: this.getScheduleDescription(false),
			}
			if (!this.bulkPayload.bulk) {
				this.bulkPayload.bulk = {};
			}
			this.bulkPayload.bulk.nextRunDate = this.form.value.startDate;

			this.modalRef.close(this.bulkPayload);
		}
	}

	getScheduleDescription(bold?: boolean): string | undefined {
		if (!this.form.valid) {
			return undefined;
		}

		var type = this.form.get('triggerType')?.value as CommunicationTriggerTypeEnum;
		if (type === undefined) {
			return undefined;
		}
		switch (type) {
			case 'DateOfBirth':
				return "Communication will be sent annually on the member's birthday.";
			case 'PolicyRenewalReminder':
				if (this.form.value.reminderBefore === undefined || this.form.value.reminderBefore === null
					|| this.form.value.reminderBeforeValue === undefined || this.form.value.reminderBeforeValue === null) {
						return "Communication will be sent on the member's policy renewal date.";
				}
				return `Communication will be sent ${this.form.value.reminderBeforeValue} ${this.form.value.reminderBefore} before the member's policy renewal date.`;
			case 'Recurring':
				if (this.form.value.recurring?.frequency === undefined || this.form.value.recurring?.frequency === null
					|| this.form.value?.time === undefined || this.form.value?.time === null) {
						return "Communication will be sent on a recurring schedule.";
				}
				const selectedFrequency = this.frequencies.find(f => f.value === this.form.value.recurring?.frequency);
				const frequencies = this.boldWrap(selectedFrequency?.label, bold);
				const time = this.boldWrap(this.getTimeAsString()!, bold);

				const out = `Communication will be sent: ${frequencies}, at ${time}`;

				return out;
		}

		return undefined;
	}

	boldWrap(text: string | undefined, bold?: boolean) {
		if (!text) {
			return 'Unknown';
		}
		return bold !== false ? `<strong>${text}</strong>` : text;
	}

  	cancel() {
    	this.modalRef.close(null);
  	}

	private getTimeAsString(): string | undefined {
		return this.form.value.time
			? `${this.form.value.time.getHours().toString().padStart(2, '0')}:${this.form.value.time.getMinutes().toString().padStart(2, '0')}`
			: undefined; 
	}

	private parseTimeString(timeStr: string): Date {
		const [hours, minutes] = timeStr.split(':').map(Number);
		const date = new Date();
		date.setHours(hours || 0, minutes || 0, 0, 0);
		return date;
	}
}
